import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { CAUserDto } from 'src/core/models/administration/user/CAUser.dto';
import { UserService } from '../../services/user.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService } from 'src/core/services/fileupload/fileupload.service';

@Component({
    selector: 'ca-user-list-selector',
    templateUrl: './user-list-selector.component.html',
    styleUrls: ['./user-list-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => UserListSelectorComponent),
        },
    ],
    standalone: false
})
export class UserListSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input()
  userType: 'all' | 'agent' | 'evaluator' = 'all';

  @Input()
  quickSearchTerm: string | null = null;

  @Input()
  departmentBased: boolean = true;

  @Input()
  departmentId: number | null = null;

  @Input()
  status: 'all' | 'active' | 'passive' = 'active';

  @ViewChild('chkAll')
  chkAll: ElementRef;

  items: CAUserDto[] = [];
  totalCount: number = 0;
  loading: boolean = false;
  loadingForSelectingAll = false;
  isDisabled: boolean = false;
  value: CAUserDto[] = [];

  private pageIndex: number = 0;
  private pageSize: number = 25;
  private _userProfileThumbnailsCached: boolean = false;

  get userProfileThumbnailsCached(): boolean {
    return this._userProfileThumbnailsCached;
  }

  constructor(private service: UserService, private fileUploadService: FileUploadService) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    this.pageIndex = 0;
    this.items = [];
    this.load();
  }

  writeValue(obj: CAUserDto[] | null): void {
    obj = obj ?? [];

    this.value = new Array(...obj);
    this.setChkAllState();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onChangeBase(args: any): void {
    this.setChkAllState();
    this.onChange(args);
  }

  onChange(args: any): void {}

  onTouched(): void {}

  onScroll(): void {
    this.pageIndex++;
    this.load(true);
  }

  onScrollUp(): void {}

  onSelectAllChange(eventArgs: Event): void {
    const chk = eventArgs.currentTarget as HTMLInputElement;
    const fn = chk.checked ? this.selectAll : this.deselectAll;

    fn.apply(this);
  }

  onUserSelectionChange(user: CAUserDto, eventArgs: Event): void {
    const chk = eventArgs.currentTarget as HTMLInputElement;

    if (chk.checked) {
      this.value.push(user);
    } else {
      this.value = this.value.filter(x => x.id !== user.id);
    }

    this.onChangeBase(this.value);

    eventArgs.preventDefault();
    eventArgs.stopPropagation();
  }

  isUserSelected(user: CAUserDto): boolean | null {
    return this.value.findIndex(x => x.id === user.id) > -1 ? true : null;
  }

  private selectAll(): void {
    if (this.departmentBased && this.departmentId) {
      this.value = this.value.filter(x => x.departmentId !== this.departmentId);
    }

    if (this.items.length === this.totalCount) {
      this.value = this.value.concat(this.items);
      this.onChangeBase(this.value);

      return;
    }

    this.loadingForSelectingAll = true;

    var filters = this.service.getFilter(
      this.quickSearchTerm,
      this.userType,
      this.status,
      this.departmentId
    );

    this.service.getList(filters, 0, 99999, false).subscribe(response => {
      this.value = this.value.concat(response.items);
      this.onChangeBase(this.value);

      this.loadingForSelectingAll = false;
    });
  }

  private deselectAll(): void {
    if (this.departmentBased && this.departmentId) {
      this.value = this.value.filter(x => x.departmentId !== this.departmentId);
    } else {
      this.value.splice(0, this.value.length);
    }

    this.onChangeBase(this.value);
  }

  private load(pageChange: boolean = false): void {
    if (this.departmentBased && !this.departmentId) {
      return;
    }

    this.loading = true;
    this._userProfileThumbnailsCached = false;

    var filters = this.service.getFilter(
      this.quickSearchTerm,
      this.userType,
      this.status,
      this.departmentId
    );

    this.service.getList(filters, this.pageIndex, this.pageSize, false).subscribe({
      next: response => {
        this.items = pageChange ? this.items.concat(response.items) : response.items;
        this.totalCount = response.totalCount;

        var userIds = this.items.map(u => u.id);
        this.fileUploadService.bulkDownloadProfileThumbnail(userIds, filters).subscribe(() => {
          this._userProfileThumbnailsCached = true;
        });

        this.setChkAllState();
      },
      error: e => {},
      complete: () => {
        this.loading = false;
      },
    });
  }

  setChkAllState(): void {
    if (!this.chkAll) {
      return;
    }

    let checkedNone: boolean = false;
    let checkAll: boolean = false;

    if (this.departmentBased && this.departmentId) {
      checkAll =
        this.value.filter(x => x.departmentId === this.departmentId).length === this.totalCount;
      checkedNone = this.value.filter(x => x.departmentId === this.departmentId).length === 0;
    } else {
      checkAll = this.value.length === this.totalCount;
      checkedNone = this.value.length === 0;
    }

    if (checkedNone) {
      this.chkAll.nativeElement.checked = false;
      this.chkAll.nativeElement.indeterminate = false;
    } else if (checkAll) {
      this.chkAll.nativeElement.indeterminate = false;
      this.chkAll.nativeElement.checked = true;
    } else {
      this.chkAll.nativeElement.indeterminate = true;
    }
  }
}
